import React, { ComponentProps } from 'react'
import useTranslation from 'next-translate/useTranslation'
import Trans from 'next-translate/Trans'
import { Layout } from 'components/Layout'
import { Link } from 'components/Link'
import { Header } from 'components/Header'
import { Footer } from 'components/Footer'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { TranslationKeys } from 'locales/translationKeys'
import { CollapsibleMenu } from 'features/components/CollapsibleMenu'
import { CollapsibleContentSection } from './components/CollapsibleContentSection'
import {
  PrivacyPolicyRoot,
  Introduction,
  Content,
  Wrapper,
  CollapsibleContentWrapper,
  Contact,
} from './PrivacyPolicy.styles'
import { VerticalSeparator } from 'components/VerticalSeparator'

type PrivacyPolicyOwnProps = ComponentProps<typeof PrivacyPolicyRoot>
export type PrivacyPolicyProps = PrivacyPolicyOwnProps & {}

type ParagraphsAndListsType = {
  title: TranslationKeys
  paragraphs1?: TranslationKeys[]
  bulletPoints1?: TranslationKeys[]
  numberedItems1?: TranslationKeys[]
  paragraphs2?: TranslationKeys[]
  bulletPoints2?: TranslationKeys[]
}

type SubsectionType = ParagraphsAndListsType & {
  subsections?: SubsectionType[]
  subsubsections?: SubsectionType[]
}

export type PrivacyPolicyType = ParagraphsAndListsType & {
  subsections?: SubsectionType[]
  links?: linkObject[]
}

export type linkObject = {
  link: string
  key: string
}

const PRIVACY_POLICY: PrivacyPolicyType[] = [
  {
    title: 'privacy-policy:scope.title',
    paragraphs1: ['privacy-policy:scope.description-1', 'privacy-policy:scope.description-2'],
  },
  {
    title: 'privacy-policy:changes.title',
    paragraphs1: ['privacy-policy:changes.description-1'],
  },
  {
    title: 'privacy-policy:international.title',
    paragraphs1: ['privacy-policy:international.description-1'],
  },
  {
    title: 'privacy-policy:children.title',
    paragraphs1: ['privacy-policy:children.description-1'],
    links: [{ link: 'mailto:hello@wefeedraw.com', key: '0' }],
  },
  {
    title: 'privacy-policy:collected.title',
    subsections: [
      {
        title: 'privacy-policy:collected.submitted.title',
        paragraphs1: [
          'privacy-policy:collected.submitted.description-1',
          'privacy-policy:collected.submitted.description-2',
          'privacy-policy:collected.submitted.description-3',
        ],
      },
      {
        title: 'privacy-policy:collected.collected.title',
        paragraphs1: [
          'privacy-policy:collected.collected.description-1',
          'privacy-policy:collected.collected.description-2',
        ],
      },
      {
        title: 'privacy-policy:collected.transactions.title',
        paragraphs1: ['privacy-policy:collected.transactions.description-1'],
      },
      {
        title: 'privacy-policy:collected.analytics.title',
        paragraphs1: ['privacy-policy:collected.analytics.description-1'],
        bulletPoints1: [
          'privacy-policy:collected.analytics.bullet-1',
          'privacy-policy:collected.analytics.bullet-2',
        ],
        paragraphs2: ['privacy-policy:collected.analytics.description-2'],
      },
      {
        title: 'privacy-policy:collected.cookies.title',
        paragraphs1: [
          'privacy-policy:collected.cookies.description-1',
          'privacy-policy:collected.cookies.description-2',
        ],
      },
      {
        title: 'privacy-policy:collected.marketing.title',
        paragraphs1: [
          'privacy-policy:collected.marketing.description-1',
          'privacy-policy:collected.marketing.description-2',
          'privacy-policy:collected.marketing.description-3',
        ],
      },
    ],
  },
  {
    title: 'privacy-policy:use.title',
    paragraphs1: ['privacy-policy:use.description-1'],
    subsections: [
      {
        title: 'privacy-policy:use.reasons.title',
        bulletPoints1: [
          'privacy-policy:use.reasons.bullet-1',
          'privacy-policy:use.reasons.bullet-2',
          'privacy-policy:use.reasons.bullet-3',
          'privacy-policy:use.reasons.bullet-4',
          'privacy-policy:use.reasons.bullet-5',
          'privacy-policy:use.reasons.bullet-6',
          'privacy-policy:use.reasons.bullet-7',
          'privacy-policy:use.reasons.bullet-8',
          'privacy-policy:use.reasons.bullet-9',
        ],
      },
    ],
  },
  {
    title: 'privacy-policy:disclosure.title',
    paragraphs1: ['privacy-policy:disclosure.description-1'],
    bulletPoints1: [
      'privacy-policy:disclosure.bullet-1',
      'privacy-policy:disclosure.bullet-2',
      'privacy-policy:disclosure.bullet-3',
      'privacy-policy:disclosure.bullet-4',
      'privacy-policy:disclosure.bullet-5',
      'privacy-policy:disclosure.bullet-6',
      'privacy-policy:disclosure.bullet-7',
      'privacy-policy:disclosure.bullet-8',
    ],
    paragraphs2: [
      'privacy-policy:disclosure.description-2',
      'privacy-policy:disclosure.description-3',
      'privacy-policy:disclosure.description-4',
      'privacy-policy:disclosure.description-5',
    ],
  },
  {
    title: 'privacy-policy:amend.title',
    paragraphs1: [
      'privacy-policy:amend.description-1',
      'privacy-policy:amend.description-2',
      'privacy-policy:amend.description-3',
      'privacy-policy:amend.description-4',
      'privacy-policy:amend.description-5',
      'privacy-policy:amend.description-6',
      'privacy-policy:amend.description-7',
    ],
    links: [
      { link: 'mailto:hello@wefeedraw.com', key: '0' },
      {
        link: 'https://docs.google.com/forms/d/e/1FAIpQLSe9bN-k1Qwvn8YEd8_r93mRh_hXGTRLXfZB3bUWf4KEreDdDg/viewform?usp=header',
        key: '1',
      },
    ],
  },
  {
    title: 'privacy-policy:social.title',
    paragraphs1: ['privacy-policy:social.description-1', 'privacy-policy:social.description-2'],
  },
  {
    title: 'privacy-policy:security.title',
    paragraphs1: [
      'privacy-policy:security.description-1',
      'privacy-policy:security.description-2',
      'privacy-policy:security.description-3',
    ],
  },
  {
    title: 'privacy-policy:california.title',
    paragraphs1: ['privacy-policy:california.description-1'],
    subsections: [
      {
        title: 'privacy-policy:california.no-track.title',
        paragraphs1: ['privacy-policy:california.no-track.description-1'],
      },
      {
        title: 'privacy-policy:california.ccpa.title',
        paragraphs1: ['privacy-policy:california.ccpa.description-1'],
        subsubsections: [
          {
            title: 'privacy-policy:california.information-disclosure.title',
            paragraphs1: ['privacy-policy:california.information-disclosure.description-1'],

            bulletPoints1: [
              'privacy-policy:california.information-disclosure.bullet-1',
              'privacy-policy:california.information-disclosure.bullet-2',
              'privacy-policy:california.information-disclosure.bullet-3',
              'privacy-policy:california.information-disclosure.bullet-4',
              'privacy-policy:california.information-disclosure.bullet-5',
            ],
            paragraphs2: ['privacy-policy:california.information-disclosure.description-2'],
            bulletPoints2: [
              'privacy-policy:california.information-disclosure.bullet-1',
              'privacy-policy:california.information-disclosure.bullet-2',
              'privacy-policy:california.information-disclosure.bullet-3',
            ],
          },
          {
            title: 'privacy-policy:california.sold.title',
            paragraphs1: ['privacy-policy:california.sold.description-1'],
            bulletPoints1: [
              'privacy-policy:california.sold.bullet-1',
              'privacy-policy:california.sold.bullet-2',
              'privacy-policy:california.sold.bullet-3',
            ],
          },
          {
            title: 'privacy-policy:california.deletion.title',
            paragraphs1: ['privacy-policy:california.deletion.description-1'],
            bulletPoints1: [
              'privacy-policy:california.deletion.bullet-1',
              'privacy-policy:california.deletion.bullet-2',
            ],
            paragraphs2: ['privacy-policy:california.deletion.description-2'],
            bulletPoints2: [
              'privacy-policy:california.deletion.bullet-3',
              'privacy-policy:california.deletion.bullet-4',
              'privacy-policy:california.deletion.bullet-5',
              'privacy-policy:california.deletion.bullet-6',
              'privacy-policy:california.deletion.bullet-7',
              'privacy-policy:california.deletion.bullet-8',
              'privacy-policy:california.deletion.bullet-9',
              'privacy-policy:california.deletion.bullet-10',
              'privacy-policy:california.deletion.bullet-11',
            ],
          },
          {
            title: 'privacy-policy:california.discrimination.title',
            paragraphs1: ['privacy-policy:california.discrimination.description-1'],
            bulletPoints1: [
              'privacy-policy:california.discrimination.bullet-1',
              'privacy-policy:california.discrimination.bullet-2',
              'privacy-policy:california.discrimination.bullet-3',
              'privacy-policy:california.discrimination.bullet-4',
              'privacy-policy:california.discrimination.bullet-5',
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'privacy-policy:questions.title',
    paragraphs1: [
      'privacy-policy:questions.description-1',
      'privacy-policy:questions.description-2',
    ],
    bulletPoints1: [
      'privacy-policy:questions.bullet-1',
      'privacy-policy:questions.bullet-2',
      'privacy-policy:questions.bullet-3',
    ],
    paragraphs2: ['privacy-policy:questions.description-3'],
    links: [
      { link: 'mailto:hello@wefeedraw.com', key: '0' },
      {
        link: 'https://docs.google.com/forms/d/e/1FAIpQLSe9bN-k1Qwvn8YEd8_r93mRh_hXGTRLXfZB3bUWf4KEreDdDg/viewform?usp=header',
        key: '1',
      },
    ],
  },
]

export const PrivacyPolicy = (_props: PrivacyPolicyProps) => {
  const { t } = useTranslation()

  const header = <Header />

  return (
    <PrivacyPolicyRoot>
      <Layout header={header} hideCTA>
        <Content>
          <Wrapper>
            <Stack direction="column" gap={{ '@initial': '4', '@bp2': '8' }}>
              <Stack direction="column" gap={{ '@initial': '3', '@bp2': '7' }}>
                <Stack direction="column" gap={{ '@initial': '2', '@bp2': '3' }}>
                  <Stack direction="column">
                    <Text
                      size={{ '@initial': 'h4', '@bp2': 'h3' }}
                      weight="bold"
                      color="accent-black"
                    >
                      {t('privacy-policy:title')}
                    </Text>
                    <Text size="small" color="accent-black">
                      {t('privacy-policy:subtitle-1')}
                    </Text>
                    <Text size="small" color="accent-black">
                      {t('privacy-policy:subtitle-2')}
                    </Text>
                  </Stack>
                  <Introduction>
                    <VerticalSeparator color="accent-black" />
                    <Stack direction="column" gap="3">
                      <Text color="accent-black">
                        <Trans
                          i18nKey={'privacy-policy:description-1'}
                          components={[<Link key="0" href="/" />]}
                        />
                      </Text>
                      <Text color="accent-black">{t('privacy-policy:description-2')}</Text>
                    </Stack>
                  </Introduction>
                </Stack>

                <Stack as="ol" direction="column">
                  {PRIVACY_POLICY.map(
                    (
                      {
                        title,
                        paragraphs1,
                        bulletPoints1,
                        paragraphs2,
                        bulletPoints2,
                        links,
                        subsections,
                      },
                      i
                    ) => {
                      return (
                        <Stack as="li" direction="column" key={i}>
                          <CollapsibleMenu
                            key={i}
                            label={`${i + 1}.  ${t(title)}`}
                            hasMaxWidth={false}
                          >
                            <CollapsibleContentWrapper>
                              <Stack direction="column" gap="3">
                                <CollapsibleContentSection
                                  paragraphsAndLists={[
                                    {
                                      paragraphs: paragraphs1,
                                      lists: bulletPoints1,
                                      links: links,
                                    },
                                    {
                                      paragraphs: paragraphs2,
                                      lists: bulletPoints2,
                                      links: links,
                                    },
                                  ]}
                                />
                                {subsections &&
                                  subsections.map(
                                    (
                                      {
                                        title,
                                        paragraphs1,
                                        bulletPoints1,
                                        subsubsections,
                                        paragraphs2,
                                        bulletPoints2,
                                      },
                                      j
                                    ) => {
                                      return (
                                        <Stack direction="column" key={j} gap="3">
                                          <CollapsibleContentSection
                                            number={`${i + 1}.${j + 1}`}
                                            title={t(title)}
                                            paragraphsAndLists={[
                                              {
                                                paragraphs: paragraphs1,
                                                lists: bulletPoints1,
                                                links: links,
                                              },
                                              {
                                                paragraphs: paragraphs2,
                                                lists: bulletPoints2,
                                                links: links,
                                              },
                                            ]}
                                          />
                                          {subsubsections &&
                                            subsubsections?.map(
                                              (
                                                {
                                                  title,
                                                  paragraphs1,
                                                  bulletPoints1,
                                                  paragraphs2,
                                                  bulletPoints2,
                                                },
                                                k
                                              ) => {
                                                return (
                                                  <Stack direction="column" key={k} gap="3">
                                                    <CollapsibleContentSection
                                                      nestingLevel={1}
                                                      number={`${i + 1}.${j + 1}.${k + 1}`}
                                                      title={t(title)}
                                                      paragraphsAndLists={[
                                                        {
                                                          paragraphs: paragraphs1,
                                                          lists: bulletPoints1,
                                                          links: links,
                                                        },
                                                        {
                                                          paragraphs: paragraphs2,
                                                          lists: bulletPoints2,
                                                          links: links,
                                                        },
                                                      ]}
                                                    />
                                                  </Stack>
                                                )
                                              }
                                            )}
                                        </Stack>
                                      )
                                    }
                                  )}
                              </Stack>
                            </CollapsibleContentWrapper>
                          </CollapsibleMenu>
                        </Stack>
                      )
                    }
                  )}
                </Stack>
              </Stack>

              <Contact justifyContent="center">
                <Text size="headline" weight="bold" color="accent-black" align="center">
                  <Trans
                    i18nKey={'privacy-policy:contact'}
                    components={[<Link key="0" href="mailto:hello@wefeedraw.com" />]}
                  />
                </Text>
              </Contact>
            </Stack>
          </Wrapper>
        </Content>
        <Footer />
      </Layout>
    </PrivacyPolicyRoot>
  )
}

PrivacyPolicy.displayName = 'PrivacyPolicy'
